import type { WebinarContentPiece } from "@xop/dashboard-shared";
import { Box, Skeleton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { JSX } from "react";

import { Markdown } from "../../../documentation/markdown.js";

/**
 * Previews for the two things step 3 produces.
 *
 * The operator's job here is a publishing decision — "is this good enough to
 * go out?" — and you cannot make that call against a scrollable grey textarea.
 * So each preview is shaped like the place the content is actually going: the
 * blog post reads as an article, the LinkedIn post reads as a LinkedIn post.
 * The hero art is part of the artifact, not an attachment, so it renders IN
 * the preview rather than behind a "show the picture" button.
 *
 * Everything comes from the MUI theme (per the repo's styling rule) — these
 * are XOP surfaces first and imitations second, so they borrow LinkedIn's
 * LAYOUT (which is what makes the preview informative) without pretending to
 * be a screenshot of it.
 */

/** Shared height reservation so the text never jumps when the art arrives. */
function HeroSlot({
  src,
  alt,
  ratio,
  loading,
}: {
  src: string | null;
  alt: string;
  /** CSS aspect-ratio, matching what the image was generated at. */
  ratio: string;
  loading: boolean;
}): JSX.Element | null {
  if (!src && !loading) return null;
  return (
    <Box sx={{ position: "relative", width: "100%", aspectRatio: ratio, bgcolor: "surfaceSoft" }}>
      {src ? (
        <Box
          component="img"
          src={src}
          alt={alt}
          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        <Skeleton variant="rectangular" sx={{ width: "100%", height: "100%" }} />
      )}
    </Box>
  );
}

/**
 * The blog post, as an article.
 *
 * A measured column (~68 characters), display-face headline, and article
 * rhythm — the body's own 14px/1.625 is right for a dashboard panel and wrong
 * for something you are about to publish, so it is lifted here. The wrapper's
 * descendant selectors out-specify the renderer's inline styles.
 */
export function BlogPreview({
  piece,
  imageSrc,
  imageLoading,
}: {
  piece: WebinarContentPiece;
  imageSrc: string | null;
  imageLoading: boolean;
}): JSX.Element {
  return (
    <Box
      data-testid="blog-preview"
      sx={{
        // A page, not a panel: bounded and centred so the measure below is a
        // real reading measure. Full-bleed here made the hero ~970px tall and
        // pushed the article itself off screen.
        maxWidth: 880,
        mx: "auto",
        border: 1,
        borderColor: "divider",
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: "background.paper",
        boxShadow: 1,
      }}
    >
      {/* 2:1 banner crop of the 3:2 original — what a blog hero actually is.
          The untouched PNG is one click away under Picture. */}
      <HeroSlot src={imageSrc} alt={piece.title} ratio="2 / 1" loading={imageLoading} />

      <Box sx={{ px: { xs: 3, md: 5 }, py: { xs: 3.5, md: 4.5 } }}>
        <Box sx={{ maxWidth: "68ch", mx: "auto" }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "primary.main",
              mb: 1.5,
            }}
          >
            XOP Networks · Blog
          </Typography>

          <Typography
            component="h2"
            sx={{
              fontFamily: (t) => t.typography.h1.fontFamily,
              // Fluid: a headline that is right at 1400px is oversized at 900.
              fontSize: "clamp(1.75rem, 1.2rem + 1.6vw, 2.6rem)",
              fontWeight: 600,
              lineHeight: 1.12,
              letterSpacing: "-0.02em",
              color: "text.primary",
            }}
          >
            {piece.title}
          </Typography>

          <Box
            sx={{
              mt: 3,
              pt: 3,
              borderTop: 1,
              borderColor: "divider",
              // Article rhythm. `& h2` (0-1-1) out-specifies the renderer's own
              // single-class inline sx, so these win without !important.
              "& h1, & h2, & h3": {
                fontFamily: (t) => t.typography.h1.fontFamily,
                letterSpacing: "-0.015em",
                lineHeight: 1.25,
                color: "text.primary",
              },
              "& h2": { fontSize: 22, fontWeight: 600, marginTop: "2.25rem", marginBottom: "0.5rem" },
              "& h3": { fontSize: 18, fontWeight: 600, marginTop: "1.75rem", marginBottom: "0.35rem" },
              "& p": { fontSize: 16, lineHeight: 1.7, marginTop: "1.15rem", color: "text.secondary" },
              "& li": { fontSize: 16, lineHeight: 1.65, color: "text.secondary" },
              "& ul, & ol": { marginTop: "1.15rem", paddingLeft: "1.35rem" },
              "& strong": { color: "text.primary", fontWeight: 600 },
              "& a": { color: "primary.main", textDecorationThickness: "1px" },
              // The lead paragraph carries the piece — give it presence.
              "& > p:first-of-type": {
                fontSize: 18,
                lineHeight: 1.6,
                color: "text.primary",
                marginTop: 0,
              },
            }}
          >
            <Markdown source={piece.body_markdown} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * The LinkedIn post, in LinkedIn's own order: author, then text, then the
 * image edge-to-edge underneath.
 *
 * That order is the whole point — it is how the operator can tell whether the
 * first two lines earn the "see more" click, which is the only thing that
 * decides if a LinkedIn post gets read.
 */
export function LinkedInPreview({
  piece,
  imageSrc,
  imageLoading,
}: {
  piece: WebinarContentPiece;
  imageSrc: string | null;
  imageLoading: boolean;
}): JSX.Element {
  return (
    <Box
      data-testid="linkedin-preview"
      sx={{
        // LinkedIn's feed column is ~555px. Holding to it is what makes the
        // line breaks and the "see more" fold land where they really will.
        maxWidth: 555,
        mx: "auto",
        border: 1,
        borderColor: "divider",
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: "background.paper",
        boxShadow: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, pt: 2, pb: 1.5 }}>
        <Box
          component="img"
          src="/xop-mark.svg"
          alt=""
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            bgcolor: "surfaceSoft",
            p: 0.5,
            flexShrink: 0,
          }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>
            XOP Networks
          </Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.4 }}>
            Everything over packets
          </Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.4 }}>
            Now · Draft
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 2, pb: 1.75 }}>
        <PostText text={piece.body_markdown} />
      </Box>

      <HeroSlot src={imageSrc} alt={piece.title} ratio="1.91 / 1" loading={imageLoading} />
    </Box>
  );
}

/**
 * LinkedIn body text: the platform renders plain text, so line breaks are
 * preserved verbatim and hashtags get the platform's own tint. Anything that
 * arrived as stray Markdown emphasis is stripped rather than shown — on
 * LinkedIn `**bold**` renders as literal asterisks, and seeing them here is
 * the operator's cue that the copy needs a fix.
 */
export function PostText({ text }: { text: string }): JSX.Element {
  return (
    <Typography
      component="div"
      sx={{ fontSize: 14, lineHeight: 1.5, color: "text.primary", whiteSpace: "pre-wrap" }}
    >
      {stripEmphasis(text)
        .split(/(\s+)/)
        .map((token, i) =>
          token.startsWith("#") && token.length > 1 ? (
            <Box key={i} component="span" sx={{ color: "primary.main", fontWeight: 500 }}>
              {token}
            </Box>
          ) : (
            token
          ),
        )}
    </Typography>
  );
}

/** Remove Markdown emphasis markers LinkedIn would render literally. */
export function stripEmphasis(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/(^|[^*])\*(?!\s)([^*]+?)\*/g, "$1$2")
    .replace(/^#{1,6}\s+/gm, "");
}

/** One-line summary for the collapsed row — Markdown flattened to plain text. */
export function previewLine(text: string, max = 140): string {
  const line = stripEmphasis(text)
    .split(/\r?\n/)
    .map((l) => l.replace(/^[-*>]\s*/, "").trim())
    .find((l) => l.length > 0);
  if (!line) return "";
  return line.length <= max ? line : `${line.slice(0, max).trimEnd()}…`;
}

/** Soft brand wash behind a preview, so the artifact reads as a thing being
 * previewed rather than as more page. */
export function previewStageSx() {
  return {
    p: { xs: 2, md: 3 },
    borderRadius: 1.5,
    bgcolor: (t: { palette: { primary: { main: string } } }) =>
      alpha(t.palette.primary.main, 0.04),
  };
}
